import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { selectAppOwnerId } from 'core-components/sitebuilder/store/selectors/selectAppOwnerId'
import Logger from 'core/utils/Logger'
import { showError, showSuccess } from 'onboarding/classes/services/notification'
import { useParentRequest } from 'onboarding/hooks/useParentRequest'
import { useRequest } from 'onboarding/hooks/useRequest'
import { partnershipService } from 'onboarding/services/partnershipService'

import { IPartnerShipNA } from '../types'

export const usePartnerShipNA = (setItem: (value: IPartnerShipNA) => void, rid: string) => {
  const appOwnerId = useSelector(selectAppOwnerId)
  const { req, reqWip } = useRequest(rid)
  const { parentRequest, parentRequestWip } = useParentRequest(req.Parent)
  const request = useMemo(
    () => (req.Parent && parentRequest?.EntryId === req.Parent ? parentRequest : req),
    [req, parentRequest]
  )

  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    const fetch = async () => {
      if (request.EntryId) {
        setIsLoading(true)
        const { data, error } = await partnershipService.getNAByRequestId(
          request.EntryId,
          appOwnerId
        )
        if (cancelled) {
          return
        }
        data && setItem(data)
        error && Logger.error(error)
        setIsLoading(false)
      } else {
        setIsLoading(false)
      }
    }

    fetch()
    return () => {
      cancelled = true
    }
  }, [appOwnerId, request.EntryId, setItem])

  const onSave = useCallback(
    async (item: IPartnerShipNA) => {
      setIsSaving(true)
      const response = await partnershipService.saveNA(
        {
          ...item,
          RequestId: request.EntryId,
          PartnershipType: item.PartnershipType || 'Reseller',
        },
        appOwnerId
      )
      if (response.error) {
        showError(response.error)
      } else {
        response.data && setItem(response.data)
        showSuccess('Saved')
      }

      setIsSaving(false)
    },
    [request.EntryId, appOwnerId, setItem]
  )

  return {
    isLoading,
    isSaving,
    onSave,
    request,
    isRequestLoading: reqWip || parentRequestWip,
  }
}
